import { Card, CardContent, Stack, Typography, Grid, Box, CircularProgress, Button } from '@mui/material';
import {
  TrendingUp,
  BarChart as BarChart3,
  MenuBook,
  AttachMoney,
  People as Users,
  GpsFixed as Target,
  Security
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { openModal } from 'store/reducers/actions';
import FeedbackButtons from 'components/FeedbackButtons';
import { useState } from 'react';

const getIconForMetric = (metricName) => {
  const name = metricName.toLowerCase();
  if (name.includes('accuracy') || name.includes('data')) {
    return <Target sx={{ fontSize: 40 }} />;
  } else if (name.includes('roi') || name.includes('revenue') || name.includes('cost')) {
    return <AttachMoney sx={{ fontSize: 40 }} />;
  } else if (name.includes('user') || name.includes('customer') || name.includes('client')) {
    return <Users sx={{ fontSize: 40 }} />;
  } else {
    return <TrendingUp sx={{ fontSize: 40 }} />;
  }
};

const MarketTrends = ({ marketTrends, analysisData }) => {
  const [hoveredItems, setHoveredItems] = useState({});
  const taskId = useSelector((state) => state.currentTaskReducer);
  // Get user plan from Redux store - moved outside the conditional to fix the hook rule

  const dispatch = useDispatch();
  const plan = useSelector((state) => state.proAccountReducer.plan);
  const trialWasActivated = useSelector((state) => state.proAccountReducer.trial_was_activated);
  const userCountry = useSelector((state) => state.proAccountReducer.country);
  const restrictedCountries = ['IN', 'VN', 'CN', 'BD', 'PK', 'ID'];
  const trialAllowed = !trialWasActivated && !restrictedCountries.includes(userCountry);
  const isProUser = plan === 'Pro' || plan === 'Trial' || plan === 'Pro+' || plan === 'Enterprise Pro Max' || plan === 'Enterprise Pro';

  // Handle trial click to open modal
  const handleTrialClick = () => {
    dispatch(
      openModal({
        title: 'Get access to detailed market trends',
        subtitle: "Upgrade to Pro to view all market trends and industry insights. Don't miss out on accessing more data!",
        trial: trialAllowed
      })
    );
  };

  // Return null if no data
  if (!marketTrends) return null;

  const trends = marketTrends.trends;
  const benchmarks = marketTrends.benchmarks;

  return (
    <div id="market-trends">
      <Grid container sx={{ alignItems: 'center', justifyContent: 'center' }}>
        <Stack spacing={2} sx={{ mb: 4 }} direction="row" alignItems="center" justifyContent="center">
          <Typography variant="h3">Market Trends</Typography>
          {(!trends || !benchmarks) && <CircularProgress size={24} />}
        </Stack>

        {trends && benchmarks && (
          <Stack spacing={5}>
            {/* Trends Section */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                <TrendingUp sx={{ color: 'primary.main' }} />
                <Typography variant="h4" id="market-trends-trends">
                  Key Market Trends
                </Typography>
              </Stack>

              <Stack spacing={2}>
                {/* First trend is always visible */}
                {trends.length > 0 && (
                  <Card
                    sx={{
                      borderLeft: 4,
                      borderColor: 'primary.main',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                    }}
                  >
                    <CardContent>
                      <Typography variant="h5" sx={{ mb: 1 }}>
                        {trends[0].title}
                      </Typography>
                      <Typography sx={{ mb: 2 }}>{trends[0].description}</Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{
                          bgcolor: 'grey.50',
                          py: 1,
                          px: 1.5,
                          borderRadius: 1
                        }}
                      >
                        <MenuBook sx={{ fontSize: 16, color: 'primary.main' }} />
                        <Typography variant="caption" color="text.secondary">
                          Source: {trends[0].source.document}, p.{trends[0].source.page}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {/* Remaining trends are blurred for non-pro users */}
                {trends.slice(1).map((trend, index) => {
                  const itemKey = `trend-${index}`;
                  return (
                    <Box
                      key={itemKey}
                      onMouseEnter={() => setHoveredItems((prev) => ({ ...prev, [itemKey]: true }))}
                      onMouseLeave={() => setHoveredItems((prev) => ({ ...prev, [itemKey]: false }))}
                    >
                      <Box sx={{ position: 'relative' }}>
                        <Card
                          sx={{
                            borderLeft: 4,
                            borderColor: 'primary.main',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                            filter: isProUser ? 'none' : 'blur(5px)',
                            userSelect: 'none',
                            opacity: isProUser ? 1 : 0.6
                          }}
                        >
                          <CardContent>
                            <Typography variant="h5" sx={{ mb: 1 }}>
                              {trend.title}
                            </Typography>
                            <Typography sx={{ mb: 2 }}>{trend.description}</Typography>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{
                                bgcolor: 'grey.50',
                                py: 1,
                                px: 1.5,
                                borderRadius: 1
                              }}
                            >
                              <MenuBook sx={{ fontSize: 16, color: 'primary.main' }} />
                              <Typography variant="caption" color="text.secondary">
                                Source: {trend.source.document}, p.{trend.source.page}
                              </Typography>
                            </Stack>
                          </CardContent>
                        </Card>

                        {/* Overlay for non-pro users */}
                        {!isProUser && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              zIndex: 2
                            }}
                          >
                            <Stack spacing={2} alignItems="center">
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5,
                                  justifyContent: 'center'
                                }}
                              >
                                <Security sx={{ fontSize: 28, color: 'primary.main' }} />
                                <Typography variant="h5" sx={{ fontWeight: 600, mb: 0 }}>
                                  Market Trend Details
                                </Typography>
                              </Box>
                              <Typography variant="body1" color="text.secondary" sx={{ mt: 0 }}>
                                {trialAllowed
                                  ? 'Upgrade to Pro to access all market trends and understand your industry better.'
                                  : 'Start free trial to access all market trends and understand your industry better.'}
                              </Typography>
                              <Box>
                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                                  <Button variant="gradient" color="primary" onClick={handleTrialClick}>
                                    {trialAllowed ? 'Start Free Trial' : 'Upgrade Now'}
                                  </Button>
                                </motion.div>
                              </Box>
                            </Stack>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <FeedbackButtons
                          blockId={`market-trend-${index}`}
                          blockType="market-trend"
                          blockTitle={trend.title}
                          blockContent={trend.description}
                          taskId={taskId}
                          serviceName={analysisData ? analysisData['Service Name'] : 'N/A'}
                          isVisible={!!hoveredItems[itemKey]}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Box>

            {/* Benchmarks Section */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                <BarChart3 sx={{ color: 'success.main' }} />
                <Typography variant="h4" id="market-trends-benchmarks">
                  Industry Insights
                </Typography>
              </Stack>

              <Grid container spacing={3}>
                {/* First benchmark is always visible */}
                {benchmarks.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Card
                      sx={{
                        height: '100%',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.3s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 24px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      <CardContent>
                        {/* Decorative Elements */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: 128,
                            height: 128,
                            bgcolor: 'success.light',
                            opacity: 0.1,
                            borderBottomLeftRadius: '100%'
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: 96,
                            height: 96,
                            bgcolor: 'success.light',
                            opacity: 0.1,
                            borderTopRightRadius: '100%'
                          }}
                        />

                        <Stack spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                background: (theme) =>
                                  `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              {getIconForMetric(benchmarks[0].metric_name)}
                            </Box>

                            <Box>
                              <Typography variant="h3" sx={{ lineHeight: 1.2 }}>
                                {benchmarks[0].metric_value}
                              </Typography>
                              <Typography variant="h5">{benchmarks[0].metric_name}</Typography>
                            </Box>
                          </Stack>

                          <Typography>{benchmarks[0].description}</Typography>

                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                            sx={{
                              bgcolor: 'background.paper',
                              py: 1,
                              px: 1.5,
                              borderRadius: 1,
                              border: 1,
                              borderColor: 'grey.100',
                              flexWrap: 'wrap'
                            }}
                          >
                            <MenuBook sx={{ fontSize: 16, color: 'success.main', mt: 0.5 }} />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                wordBreak: 'break-word',
                                flex: 1
                              }}
                            >
                              Source: {benchmarks[0].source.document}, p.{benchmarks[0].source.page}
                            </Typography>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Remaining benchmarks are blurred for non-pro users */}
                {benchmarks.slice(1).map((benchmark, index) => {
                  const itemKey = `benchmark-${index}`;
                  return (
                    <Grid item xs={12} md={6} key={itemKey}>
                      <Box
                        onMouseEnter={() => setHoveredItems((prev) => ({ ...prev, [itemKey]: true }))}
                        onMouseLeave={() => setHoveredItems((prev) => ({ ...prev, [itemKey]: false }))}
                      >
                        <Box sx={{ position: 'relative' }}>
                          <Card
                            sx={{
                              height: '100%',
                              position: 'relative',
                              overflow: 'hidden',
                              transition: 'all 0.3s',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 24px rgba(0,0,0,0.1)'
                              },
                              filter: isProUser ? 'none' : 'blur(5px)',
                              userSelect: 'none',
                              opacity: isProUser ? 1 : 0.6
                            }}
                          >
                            <CardContent>
                              {/* Decorative Elements */}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  right: 0,
                                  width: 128,
                                  height: 128,
                                  bgcolor: 'success.light',
                                  opacity: 0.1,
                                  borderBottomLeftRadius: '100%'
                                }}
                              />
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: 0,
                                  left: 0,
                                  width: 96,
                                  height: 96,
                                  bgcolor: 'success.light',
                                  opacity: 0.1,
                                  borderTopRightRadius: '100%'
                                }}
                              />

                              <Stack spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                  <Box
                                    sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      background: (theme) =>
                                        `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                                      color: 'white',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    {getIconForMetric(benchmark.metric_name)}
                                  </Box>

                                  <Box>
                                    <Typography variant="h3" sx={{ lineHeight: 1.2 }}>
                                      {benchmark.metric_value}
                                    </Typography>
                                    <Typography variant="h5">{benchmark.metric_name}</Typography>
                                  </Box>
                                </Stack>

                                <Typography>{benchmark.description}</Typography>

                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="flex-start"
                                  sx={{
                                    bgcolor: 'background.paper',
                                    py: 1,
                                    px: 1.5,
                                    borderRadius: 1,
                                    border: 1,
                                    borderColor: 'grey.100',
                                    flexWrap: 'wrap'
                                  }}
                                >
                                  <MenuBook sx={{ fontSize: 16, color: 'success.main', mt: 0.5 }} />
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      wordBreak: 'break-word',
                                      flex: 1
                                    }}
                                  >
                                    Source: {benchmark.source.document}, p.{benchmark.source.page}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </CardContent>
                          </Card>

                          {/* Overlay for non-pro users */}
                          {!isProUser && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                zIndex: 2
                              }}
                            >
                              <Stack spacing={2} alignItems="center">
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Security sx={{ fontSize: 28, color: 'primary.main' }} />
                                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 0 }}>
                                    Industry Insight Details
                                  </Typography>
                                </Box>
                                <Typography variant="body1" color="text.secondary" sx={{ mt: 0, textAlign: 'center' }}>
                                  {trialAllowed
                                    ? 'Upgrade to Pro to access all industry insights and understand your market better.'
                                    : 'Start free trial to access all industry insights and understand your market better.'}
                                </Typography>
                                <Box>
                                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                                    <Button variant="gradient" color="primary" onClick={handleTrialClick}>
                                      {trialAllowed ? 'Start Free Trial' : 'Upgrade Now'}
                                    </Button>
                                  </motion.div>
                                </Box>
                              </Stack>
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                          <FeedbackButtons
                            blockId={`market-benchmark-${index}`}
                            blockType="market-benchmark"
                            blockTitle={benchmark.metric_name}
                            blockContent={benchmark.description}
                            taskId={taskId}
                            serviceName={analysisData ? analysisData['Service Name'] : 'N/A'}
                            isVisible={!!hoveredItems[itemKey]}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </Stack>
        )}
      </Grid>
    </div>
  );
};

export default MarketTrends;
